import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTenantStore } from '../stores/useTenantStore';
import { useAuthStore } from '../../features/auth/store';
import { merchantsApi } from '../../features/merchants/api/merchantsApi';
import type { Merchant } from '../../features/merchants/types';

const TENANT_SCOPED_PATHS = ['/products', '/orders', '/homepage-slider', '/product-metadata'];
const isTenantScoped = (pathname: string) =>
  TENANT_SCOPED_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));

export const MerchantSelectorModal = () => {
  const { isSelectorOpen, setActiveMerchant, closeSelector } = useTenantStore();
  const setSelectedTenantId = useAuthStore((s) => s.setSelectedTenantId);
  const navigate = useNavigate();
  const location = useLocation();

  // Two stores hold the "current merchant": useTenantStore.activeMerchant (read by Orders,
  // Dashboard, Payouts, the Header label) and useAuthStore.selectedTenantId (read by Booking
  // Services, Custom Orders, Homepage Slider, and the x-tenant-id request header). Any control
  // that switches merchant must set BOTH, or the two diverge and a write can land on the wrong
  // tenant while the UI shows another. This picker sets both.
  const selectMerchant = (merchant: Merchant) => {
    setActiveMerchant(merchant);
    setSelectedTenantId(merchant.id);
  };
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isSelectorOpen) return;
    setLoading(true);
    merchantsApi.list()
      .then(setMerchants)
      .catch(() => setMerchants([]))
      .finally(() => setLoading(false));
  }, [isSelectorOpen]);

  if (!isSelectorOpen) return null;

  const filtered = merchants.filter((m) =>
    m.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      <div className="relative bg-card rounded-[20px] shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-[#E5E7EB]">
          <h2 className="text-[18px] font-[700] text-foreground">Select a Merchant Workspace</h2>
          <p className="text-[13px] text-muted-foreground mt-1">
            Choose which merchant's data you want to view.
          </p>
        </div>

        {/* Search */}
        <div className="px-6 pt-4">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              width="16" height="16" viewBox="0 0 24 24" fill="none"
            >
              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
              <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              autoFocus
              type="text"
              placeholder="Search merchants..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-[#D9D9D9] bg-muted rounded-[10px] text-[14px] text-foreground focus:outline-none focus:ring-1 focus:ring-[#28282A]"
            />
          </div>
        </div>

        {/* List */}
        <div className="px-6 py-3 max-h-[300px] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-5 h-5 border-2 border-[#28282A] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-[13px] text-muted-foreground py-8">
              {search ? 'No merchants match your search' : 'No merchants available'}
            </p>
          ) : (
            <ul className="space-y-1">
              {filtered.map((merchant) => (
                <li key={merchant.id}>
                  <button
                    onClick={() => selectMerchant(merchant)}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-[10px] hover:bg-muted text-left transition-colors group"
                  >
                    <div>
                      <p className="text-[14px] font-[600] text-foreground">{merchant.name}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{merchant.slug}</p>
                    </div>
                    <span className={`text-[11px] font-[600] px-2 py-0.5 rounded-full ${
                      merchant.active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {merchant.active ? 'Active' : 'Inactive'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#E5E7EB]">
          <button
            onClick={() => {
              closeSelector();
              if (isTenantScoped(location.pathname)) navigate('/');
            }}
            className="w-full py-2.5 rounded-[10px] border border-[#D9D9D9] text-[13px] font-medium text-muted-foreground hover:bg-muted hover:border-[#28282A] hover:text-foreground transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
